import { DateTime } from 'luxon';
import logger from '../../utils/logging/logger';
import { hasCheckedInRecord } from '../../utils/attendance/attendanceSessionStatus';

// Helper to detect if an error is likely due to a deployment
export const isDeploymentRelatedError = (error) => {
  if (!error) return false;

  // Check for common deployment-related error patterns
  const errorMessage = error.message || error.toString() || '';
  const errorStatus = error.status || error.response?.status;

  // 503 Service Unavailable - common during deployments
  if (errorStatus === 503) return true;

  // Network errors during deployment
  if (errorMessage.includes('Failed to fetch') ||
      errorMessage.includes('NetworkError') ||
      errorMessage.includes('Network request failed')) {
    return true;
  }

  // HTML returned instead of JSON (server restarting, returning error page)
  if (errorMessage.includes('Unexpected token') ||
      errorMessage.includes('SyntaxError') ||
      errorMessage.includes('<!DOCTYPE') ||
      errorMessage.includes('<html')) {
    return true;
  }

  // Chunk loading errors
  if (errorMessage.includes('Loading chunk') ||
      errorMessage.includes('ChunkLoadError')) {
    return true;
  }

  return false;
};

// Helper function to get session status
export const getSessionStatus = (session) => {
  // Checked-in state should always win, even after window closes
  if (hasCheckedInRecord(session)) {
    return 'checked-in';
  }

  // Show closed state only for students who did not check in
  if (
    session?.isPast ||
    session?.status === 'ended' ||
    session?.status === 'closed' ||
    session?.current_status === 'ended' ||
    session?.current_status === 'closed'
  ) {
    return 'ended';
  }

  return 'available';
};

export const getCheckedInTimeLabel = (session) => {
  const source = session.checked_in_at_utc || session.last_checkin;
  if (!source) return null;

  try {
    let checkedInTime;
    if (typeof source === 'string') {
      checkedInTime = DateTime.fromISO(source, { zone: 'utc' }).setZone('America/Chicago');
    } else {
      checkedInTime = DateTime.fromJSDate(source, { zone: 'utc' }).setZone('America/Chicago');
    }

    if (checkedInTime.isValid) {
      return checkedInTime.toFormat('h:mm a');
    }
  } catch (e) {
    logger.warn('Failed to parse check-in time', { source, error: e.message });
  }

  return null;
};

export const extractWeekNumber = (title) => {
  if (!title) return null;
  const match = String(title).match(/week\s*#?\s*(\d+)/i);
  return match ? Number(match[1]) : null;
};

export const getTimeRangeLabel = (session) => {
  if (!session.start_time || !session.end_time) return 'In progress now';
  const start = DateTime.fromISO(session.start_time, { zone: 'utc' }).setZone('America/Chicago');
  const end = DateTime.fromISO(session.end_time, { zone: 'utc' }).setZone('America/Chicago');
  if (!start.isValid || !end.isValid) return 'In progress now';
  return `${start.toFormat('h:mm a')} – ${end.toFormat('h:mm a')}`;
};

export const isCheckInWindowActive = (session) => {
  if (!session.start_time || !session.end_time) return false;
  const now = DateTime.now().setZone('America/Chicago');
  const start = DateTime.fromISO(session.start_time, { zone: 'utc' }).setZone('America/Chicago');
  const end = DateTime.fromISO(session.end_time, { zone: 'utc' }).setZone('America/Chicago');
  if (!start.isValid || !end.isValid) return false;
  return now >= start && now <= end;
};

export const isLiveSessionActive = (session) => {
  if (!session.virtual_start_time || !session.virtual_end_time) return false;
  const now = DateTime.now().setZone('America/Chicago');
  const start = DateTime.fromFormat(String(session.virtual_start_time).slice(0, 5), 'HH:mm', { zone: 'America/Chicago' });
  const end = DateTime.fromFormat(String(session.virtual_end_time).slice(0, 5), 'HH:mm', { zone: 'America/Chicago' });
  if (!start.isValid || !end.isValid) return false;
  const startToday = now.set({ hour: start.hour, minute: start.minute, second: 0, millisecond: 0 });
  let endToday = now.set({ hour: end.hour, minute: end.minute, second: 0, millisecond: 0 });
  if (endToday <= startToday) endToday = endToday.plus({ days: 1 });
  return now >= startToday && now <= endToday;
};

export const getVirtualTimeRangeLabel = (session) => {
  const formatTime = (value) => {
    if (!value) return null;
    const parsed = DateTime.fromFormat(String(value).slice(0, 5), 'HH:mm');
    return parsed.isValid ? parsed.toFormat('h:mm a') : null;
  };
  const start = formatTime(session.virtual_start_time);
  const end = formatTime(session.virtual_end_time);
  if (start && end) return `${start} – ${end}`;
  if (start) return `Starts ${start}`;
  if (end) return `Ends ${end}`;
  return 'In progress now';
};
